import { useTranslation } from 'react-i18next';

import {
  applyUpdate,
  dismissUpdateBanner,
  useNewVersion,
  useUpdateAvailable,
} from '../features/pwa/updateStore';

/**
 * 실시간 앱 업데이트 알림 배너.
 *
 * 모든 플랫폼(iPhone, Android PWA, Mac, Windows, 브라우저)에서
 * 새 버전 감지 시 최상단에 sticky로 표시되며, 버전 정보를 포함한다.
 *
 * 확인(지금 업데이트) 시 활성화 함수를 호출하고 즉시 새 버전으로 리로드된다.
 */
export function UpdateBanner() {
  const { t } = useTranslation();
  const updateAvailable = useUpdateAvailable();
  const newVersion = useNewVersion();

  if (!updateAvailable) return null;

  return (
    <aside
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 border-b border-[var(--accent)]/30 bg-[var(--bg-surface-elevated)]/95 px-4 pb-2.5 pt-[calc(var(--safe-top)+0.625rem)] shadow-md backdrop-blur-md transition-all text-xs"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="flex-shrink-0 inline-flex items-center rounded-full bg-[var(--accent)]/20 px-2 py-0.5 font-mono text-[11px] font-semibold text-[var(--accent)]">
          {newVersion ? `v${newVersion}` : 'NEW'}
        </span>
        <span className="truncate font-medium text-[var(--text-primary)]">
          {newVersion
            ? t('update.availableWithVersion', { version: newVersion })
            : t('update.available')}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          className="pressable rounded-md bg-[var(--accent)] px-3 py-1 font-semibold text-white shadow-sm hover:opacity-95 active:scale-95 transition-all"
          onClick={() => {
            void applyUpdate();
          }}
        >
          {t('update.reload')}
        </button>
        <button
          type="button"
          aria-label={t('common.close')}
          className="pressable rounded-md p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] active:scale-90 transition-all"
          onClick={dismissUpdateBanner}
        >
          ✕
        </button>
      </div>
    </aside>
  );
}
